import Seo from "@/components/Seo";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, FileText, ArrowUpRight, Info } from "lucide-react";
import { COMPANY } from "@/data/legalContent";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const LinkRow = ({ links }) => (
  <div className="mt-3 flex flex-wrap gap-2.5">
    {links.map((l) => (
      <Link
        key={l.to}
        to={l.to}
        data-testid={`legal-inline-link-${l.to.replace(/\//g, "")}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#ecfdf3] px-4 py-2 text-sm font-bold text-[#16a34a] hover:bg-[#16a34a] hover:text-white transition-colors"
      >
        {l.label} <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    ))}
  </div>
);

const Block = ({ block }) => {
  if (block.linkRow) return <LinkRow links={block.linkRow} />;
  if (block.sub !== undefined) {
    return (
      <div className="mt-6">
        {block.sub && <h4 className="font-display text-base font-bold text-[#091630] mb-2">{block.sub}</h4>}
        {block.p && <p className="text-[15px] text-slate-600 leading-relaxed">{block.p}</p>}
        {block.list && <BulletList items={block.list} />}
      </div>
    );
  }
  if (block.list) return <BulletList items={block.list} />;
  return <p className="text-[15px] text-slate-600 leading-relaxed mt-3 first:mt-0">{block.p}</p>;
};

const BulletList = ({ items }) => (
  <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-2.5 text-[15px] text-slate-600 leading-relaxed">
        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#21c55e] shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function LegalPage({ doc }) {
  const [active, setActive] = useState("");

  const toc = useMemo(
    () => doc.sections.map((s) => ({ id: slugify(s.h), label: s.h })),
    [doc]
  );

  useEffect(() => {
    const onScroll = () => {
      let current = "";
      for (const item of toc) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= 140) current = item.id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [toc]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 110, behavior: "smooth" });
  };

  return (
    <main data-testid={`legal-${doc.slug}`} className="pt-24">
      <Seo path={`/${doc.slug}`} />
      {/* Header */}
      <section className="relative overflow-hidden bg-[#091630] py-16 sm:py-20">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#21c55e]/15 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-[#06b6d4]/15 blur-3xl" />
        <div className="absolute inset-0 dotted-bg opacity-[0.12]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold text-[#21c55e]">
            <FileText className="h-3.5 w-3.5" /> Legal
          </span>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            {doc.title}
          </h1>
          <p className="mt-4 text-sm text-slate-400">Last updated: {doc.updated}</p>
        </div>
      </section>

      <section className="relative py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[260px_1fr] gap-10">
          {/* TOC */}
          <aside className="hidden lg:block">
            <nav data-testid="legal-toc" className="sticky top-28 max-h-[calc(100vh-9rem)] overflow-y-auto pr-2">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 mb-3">On this page</p>
              <ul className="space-y-1 border-l border-slate-100">
                {toc.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollTo(item.id)}
                      className={`block w-full text-left text-sm leading-snug py-1.5 pl-4 -ml-px border-l-2 transition-colors ${
                        active === item.id
                          ? "border-[#21c55e] text-[#091630] font-semibold"
                          : "border-transparent text-slate-500 hover:text-[#091630]"
                      }`}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Body */}
          <article className="min-w-0">
            {doc.intro.map((p, i) => (
              <p key={i} className="text-base text-slate-600 leading-relaxed mt-4 first:mt-0">{p}</p>
            ))}

            {doc.showCompany && (
              <div className="mt-8 rounded-3xl border border-slate-100 bg-[#f7faf8] p-6 sm:p-7">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#16a34a] mb-3">Operated by</p>
                <p className="font-display text-lg font-bold text-[#091630]">{COMPANY.name}</p>
                {COMPANY.lines.map((l) => (
                  <p key={l} className="text-sm text-slate-500 mt-0.5">{l}</p>
                ))}
                <p className="text-sm text-slate-500 mt-2">
                  Support: <a href={`mailto:${COMPANY.support}`} className="text-[#16a34a] font-semibold">{COMPANY.support}</a>
                </p>
              </div>
            )}

            <div className="mt-10 space-y-10">
              {doc.sections.map((s) => (
                <section key={s.h} id={slugify(s.h)} className="scroll-mt-28">
                  <h2 className="font-display text-xl sm:text-2xl font-bold text-[#091630]">{s.h}</h2>
                  <div className="mt-2">
                    {s.blocks.map((b, i) => <Block key={i} block={b} />)}
                  </div>
                </section>
              ))}
            </div>

            {doc.notice && (
              <div className="mt-12 rounded-3xl border border-[#21c55e]/25 bg-[#ecfdf3] p-6 sm:p-7 flex gap-4">
                <ShieldCheck className="h-6 w-6 text-[#16a34a] shrink-0" />
                <div>
                  <p className="font-display text-sm font-bold text-[#091630] mb-1">Important notice</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{doc.notice}</p>
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center gap-2 text-xs text-slate-400">
              <Info className="h-3.5 w-3.5" />
              This summary is provided for general guidance and does not constitute legal advice.
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}
